import { type FormEvent, useState } from "react";

import { type ParseMealView, api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { mealsCopy } from "../lib/i18n/meals";
import { ScreenShell } from "./screen-shell";
import { IconSparkle } from "./ui/icon";
import { Eyebrow, H1, TopBar } from "./ui/primitives";

type Props = {
  onParsed: (parsed: ParseMealView) => void;
  onCancel: () => void;
};

const MAX_LENGTH = 500;

export function MealEntryScreen({ onParsed, onCancel }: Props) {
  const { lang } = useLang();
  const t = mealsCopy[lang];
  // Quick-insert suggestions per B_AddEntry2 design (handoff screens-b.jsx).
  // Tapping appends the chip text to whatever's already typed.
  const QUICK_INSERTS = t.entryQuickInserts;
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const parsed = await api.parseMeal(text);
      if (parsed.items.length === 0) {
        setError(t.entryNoFood);
        return;
      }
      onParsed(parsed);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.entryGenericError);
    } finally {
      setSubmitting(false);
    }
  }

  function appendQuick(s: string) {
    setText((prev) => {
      const trimmed = prev.trim();
      const next = trimmed.length === 0 ? s : `${trimmed}, ${s}`;
      return next.slice(0, MAX_LENGTH);
    });
  }

  return (
    <ScreenShell hideHeader>
      <TopBar back={t.entryBack} title={t.entryTitle} onBack={onCancel} />

      <div style={{ marginTop: 18 }}>
        <H1 accent={t.entryH1Accent}>{t.entryH1}</H1>
        <p
          className="text-[14px] leading-snug max-w-[280px]"
          style={{ marginTop: 10, color: "var(--ink-2)" }}
        >
          {t.entryLead}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col" style={{ marginTop: 24 }}>
        {/* Terminal-style input on dark surface (B_AddEntry2). */}
        <div
          className="relative"
          style={{
            padding: 18,
            borderRadius: 14,
            background: "var(--ink)",
            color: "var(--bg)",
            boxShadow: "0 10px 24px rgba(0,0,0,0.10)",
          }}
        >
          <div
            style={{
              color: "var(--accent)",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              letterSpacing: "0.1em",
              marginBottom: 8,
            }}
          >
            {t.entryTerminalLabel}
          </div>
          <div className="relative">
            <textarea
              required
              rows={4}
              maxLength={MAX_LENGTH}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t.entryPlaceholder}
              aria-label={t.entryTextareaAria}
              className="w-full resize-none outline-none"
              style={{
                background: "transparent",
                color: "var(--bg)",
                fontFamily: "var(--font-mono)",
                // 16px — see primitives.tsx Input/Textarea. The terminal
                // panel reads slightly bulkier than the original 13px on
                // desktop, but anything under 16 triggers iOS Safari /
                // WKWebView auto-zoom-on-focus that never resets, shifting
                // the whole page left and clipping the title (user-reported
                // regression on TestFlight). lineHeight tightened from 1.65
                // → 1.45 to compensate for the larger glyphs.
                fontSize: 16,
                lineHeight: 1.45,
                caretColor: "var(--accent)",
                border: "none",
                padding: 0,
                // Defensive: stop long unbroken tokens (URLs, German
                // compounds) from forcing the textarea's intrinsic min-
                // content width past w-full and rippling the layout into
                // a horizontal-scroll page on iOS WebKit.
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            />
            {/* Decorative blink cursor — only visible when empty so it acts as
                a placeholder accent without fighting the real caret. */}
            {text.length === 0 ? (
              <span
                aria-hidden="true"
                className="animate-blink"
                style={{
                  position: "absolute",
                  top: 4,
                  left: 0,
                  display: "inline-block",
                  width: 8,
                  height: 14,
                  background: "var(--accent)",
                  pointerEvents: "none",
                }}
              />
            ) : null}
          </div>
          <div
            className="flex justify-between"
            style={{
              marginTop: 14,
              paddingTop: 12,
              borderTop: "1px solid rgba(255,255,255,0.08)",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "rgba(255,255,255,0.5)",
            }}
          >
            <span>Syto Sense</span>
            <span className="tabular">
              {text.length}/{MAX_LENGTH}
            </span>
          </div>
        </div>

        {/* Quick-insert chips. */}
        <div style={{ marginTop: 18 }}>
          <Eyebrow>{t.entryQuickLabel}</Eyebrow>
          <div className="flex flex-wrap gap-1.5" style={{ marginTop: 10 }}>
            {QUICK_INSERTS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => appendQuick(t)}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 transition-colors disabled:opacity-50"
                style={{
                  padding: "6px 10px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--ink)",
                  background: "var(--surface)",
                  borderRadius: 8,
                  boxShadow: "0 0 0 1px var(--nav-border)",
                }}
              >
                <span style={{ color: "var(--accent)", fontWeight: 700 }}>+</span> {t}
              </button>
            ))}
          </div>
        </div>

        {error ? (
          <p
            className="text-[12px] px-3 py-2 rounded-md"
            style={{ background: "var(--bad-bg)", color: "var(--bad-fg)", marginTop: 16 }}
          >
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting || text.trim().length < 2}
          className="w-full inline-flex items-center justify-center gap-2 uppercase transition-all disabled:opacity-50"
          style={{
            marginTop: 22,
            padding: "17px 20px",
            borderRadius: 12,
            background: "var(--accent)",
            color: "var(--accent-ink)",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.02em",
            boxShadow: "var(--shadow-cta)",
            border: "none",
          }}
        >
          <IconSparkle size={13} />
          {submitting ? t.entryAnalyzing : t.entrySubmit}
        </button>
      </form>
    </ScreenShell>
  );
}
